import React from "react";
import { Space, Button, Spin, Checkbox, Tooltip } from "antd";
import { SendOutlined } from "@ant-design/icons";

/**
 * 邮件编辑器底部组件 - 包含发送选项和操作按钮
 *
 * 设计理念：
 * - 配置驱动：根据visibleOptions动态渲染选项
 * - 统一体验：所有选项使用一致的UI组件
 * - 扩展性强：新增选项只需修改emailTypeConfig
 * - 简洁优先：使用 Checkbox 而非 Switch，减少 footer 视觉负担
 *
 * @param {Object} props
 * @param {function} props.onCancel - 取消按钮回调
 * @param {function} props.onOk - 发送按钮回调
 * @param {boolean} props.loading - 是否显示loading状态
 * @param {string} props.loadingText - loading时显示的文本
 * @param {boolean} props.cancelDisabled - 是否禁用取消按钮
 * @param {Array<Object>} props.visibleOptions - 可见的发送选项列表
 *   每个选项对象包含：
 *   - type: string (选项类型）
 *   - label: string (显示标签）
 *   - description: string (tooltip描述）
 *   - value: boolean (当前值）
 *   - editable: boolean (是否可编辑）
 *   - required: boolean (是否强制开启）
 * @param {function} props.onOptionChange - 选项变化回调 (optionType, value) => void
 */
const EmailEditorFooter = ({ onCancel, onOk, loading = false, loadingText = "正在发送...", cancelDisabled = false, visibleOptions = [], onOptionChange }) => {
    // 统一的选项渲染
    const renderOption = (option) => {
        const { type, label, description, value, editable, required } = option;

        return editable ? (
            // 可编辑模式：显示Checkbox（比Switch更简洁）
            <Tooltip key={type} title={description}>
                <Checkbox
                    checked={value}
                    disabled={!editable}
                    onChange={(e) => onOptionChange?.(type, e.target.checked)}
                    style={{
                        fontSize: "14px",
                        userSelect: "none",
                    }}
                >
                    {label}
                    {required && (
                        <span style={{ marginLeft: 4, color: "#ff4d4f", fontSize: "12px" }}>
                            *
                        </span>
                    )}
                </Checkbox>
            </Tooltip>
        ) : (
            // 只读模式：显示文本状态
            <Space key={type} style={{ color: value ? "#1890ff" : "#999", fontSize: "14px" }}>
                <span style={{ fontWeight: 500 }}>{value ? "✓" : "○"}</span>
                <span>{label}</span>
                {required && (
                    <Tooltip title="此选项已强制开启，无法关闭">
                        <span style={{ marginLeft: 4, color: "#ff4d4f" }}>*</span>
                    </Tooltip>
                )}
            </Space>
        );
    };

    return (
        <div className="email-editor-footer">
            <Space direction="vertical" size="small" style={{ width: "100%" }}>
                {/* 发送进度提示 */}
                {/* 发送进度提示 - 已移除，改为全局遮罩显示 */}

                {/* 按钮区域 */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0 16px" }}>
                    {/* 左侧配置区：发送选项 */}
                    <Space size="middle">{visibleOptions.map(renderOption)}</Space>

                    {/* 右侧按钮区 */}
                    <Space>
                        <Button onClick={onCancel} disabled={cancelDisabled || loading}>
                            取消
                        </Button>
                        <Button type="primary" onClick={onOk} icon={<SendOutlined />} loading={loading} disabled={loading}>
                            {loading ? "发送中..." : "发送邮件"}
                        </Button>
                    </Space>
                </div>
            </Space>
        </div>
    );
};

export default EmailEditorFooter;
